import React from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HotelFormData } from "../utils/hotelSchema";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import LanguageSelect from "@/components/common/LanguageSelect";

interface ChatSettingsFormProps {
  canUseAiAssistant: boolean;
  canUseGuestCommunication: boolean;
  control: Control<HotelFormData>;
  errors: Record<string, unknown>;
}

const ChatSettingsForm: React.FC<ChatSettingsFormProps> = ({
  canUseAiAssistant,
  canUseGuestCommunication,
  control,
  errors,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 border-t pt-6 mt-6">
      <h3 className="text-lg font-medium">{t("hotels.form.chatSettings")}</h3>
      {!canUseGuestCommunication ? (
        <p className="text-sm text-muted-foreground">
          {t("hotels.form.chatSettingsUnavailable")}
        </p>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex items-center justify-between space-x-2 border p-3 rounded-md">
          <Label
            htmlFor="chatSettings.isAiEnabled"
            className="flex flex-col space-y-1"
          >
            <span>{t("hotels.form.enableAi")}</span>
            <span className="font-normal text-xs text-muted-foreground">
              {t("hotels.form.enableAiDescription")}
            </span>
          </Label>
          <Controller
            name="chatSettings.isAiEnabled"
            control={control}
            render={({ field }) => (
              <Switch
                id="chatSettings.isAiEnabled"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={!canUseAiAssistant}
              />
            )}
          />
        </div>

        <div className="flex items-center justify-between space-x-2 border p-3 rounded-md">
          <Label
            htmlFor="chatSettings.isTranslationEnabled"
            className="flex flex-col space-y-1"
          >
            <span>{t("hotels.form.enableTranslation")}</span>
            <span className="font-normal text-xs text-muted-foreground">
              {t("hotels.form.enableTranslationDescription")}
            </span>
          </Label>
          <Controller
            name="chatSettings.isTranslationEnabled"
            control={control}
            render={({ field }) => (
              <Switch
                id="chatSettings.isTranslationEnabled"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={!canUseGuestCommunication}
              />
            )}
          />
        </div>

        <Controller
          name="chatSettings.defaultLanguage"
          control={control}
          render={({ field }) => (
            <LanguageSelect
              className="col-span-2"
              label={t("hotels.form.defaultChatLanguage")}
              value={field.value}
              onChange={field.onChange}
              error={
                (
                  errors.chatSettings as
                    | { defaultLanguage?: { message?: string } }
                    | undefined
                )?.defaultLanguage?.message
              }
              disabled={!canUseGuestCommunication}
            />
          )}
        />
      </div>
    </div>
  );
};

export default ChatSettingsForm;
